import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      localStorage.setItem("ekal_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: resetEmail });
      setResetSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field =
    "bg-[#0A0C10] border-white/15 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#FF3B30] rounded-none h-12";

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#0A0C10] flex items-center justify-center px-4 grain relative">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-sm"
      >
        <Link to="/" data-testid="login-brand" className="flex items-center gap-2.5 justify-center mb-10">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF3B30]" />
          </span>
          <span className="font-heading font-extrabold text-2xl tracking-tight uppercase text-slate-100">
            Ekal <span className="text-[#FF3B30]">Sports</span> Media
          </span>
        </Link>
        <div className="border border-white/10 bg-[#12151C] p-8">
          {mode === "login" ? (
            <form onSubmit={submit} data-testid="login-form" className="space-y-4">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-400">Restricted — Press Desk</p>
              <h1 className="font-heading font-extrabold uppercase tracking-tight text-3xl text-slate-50">
                Admin Login
              </h1>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={field}
              />
              <Input
                data-testid="login-password-input"
                type="password"
                required
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className={field}
              />
              {error && (
                <p data-testid="login-error" className="text-xs font-mono text-[#FF3B30]">
                  {error}
                </p>
              )}
              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-4 transition-colors"
              >
                {loading ? "Checking credentials…" : "Enter The Desk"}
              </button>
              <div className="flex items-center justify-between">
                <Link
                  to="/"
                  data-testid="login-back-link"
                  className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300 transition-colors"
                >
                  ← Back to site
                </Link>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => {
                    setMode("forgot");
                    setError("");
                  }}
                  className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-400 hover:text-amber-300 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={sendReset} data-testid="forgot-form" className="space-y-4">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-400">Restricted — Press Desk</p>
              <h1 className="font-heading font-extrabold uppercase tracking-tight text-3xl text-slate-50">
                Reset Password
              </h1>
              {resetSent ? (
                <p data-testid="forgot-success" className="text-xs font-mono text-emerald-400 leading-relaxed">
                  If that email is registered, a reset link is on its way to the inbox. It works once and expires in 1 hour.
                </p>
              ) : (
                <>
                  <Input
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    placeholder="Admin email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className={field}
                  />
                  {error && (
                    <p data-testid="forgot-error" className="text-xs font-mono text-[#FF3B30]">
                      {error}
                    </p>
                  )}
                  <button
                    data-testid="forgot-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#FF3B30] hover:bg-red-600 disabled:opacity-60 text-white text-xs font-mono uppercase tracking-[0.2em] px-5 py-4 transition-colors"
                  >
                    {loading ? "Sending…" : "Send Reset Link"}
                  </button>
                </>
              )}
              <button
                type="button"
                data-testid="forgot-back-btn"
                onClick={() => {
                  setMode("login");
                  setError("");
                  setResetSent(false);
                }}
                className="block w-full text-center text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300 transition-colors"
              >
                ← Back to login
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
